import React, { useState, useEffect } from 'react';
import axios from 'axios';
import usePagination from '../../../utils/usePagination';
import Pagination from '../../../components/common/Pagination';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const EMPTY = { transaction_type: 'expense', category: '', amount: '', description: '', transaction_date: new Date().toISOString().split('T')[0], crop_id: '', land_id: '', payment_method: 'cash', reference_no: '' };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };

const FALLBACK_EXPENSE_CATS = ['Labor', 'Fertilizer', 'Pesticide', 'Seeds', 'Irrigation', 'Machinery', 'Fuel', 'Lease/Rent', 'Electricity', 'Transport', 'Other'];
const FALLBACK_INCOME_CATS = ['Harvest Sale', 'Subsidy', 'Crop Insurance', 'Other'];

const FinanceTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [crops, setCrops] = useState([]);
  const [lands, setLands] = useState([]);
  const [locations, setLocations] = useState([]);
  const [landsWithLoc, setLandsWithLoc] = useState([]);
  const [expenseCats, setExpenseCats] = useState(FALLBACK_EXPENSE_CATS);
  const [incomeCats, setIncomeCats] = useState(FALLBACK_INCOME_CATS);
  const [settingsCrops, setSettingsCrops] = useState([]);
  const [catsLoaded, setCatsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  // Group expense state
  const [groupMode, setGroupMode] = useState(false);
  const [groupLocationId, setGroupLocationId] = useState('');

  const fetchAll = () => {
    Promise.all([
      axios.get(`${API}/agriculture/finance`, { headers: headers() }),
      axios.get(`${API}/agriculture/crops`, { headers: headers() }),
      axios.get(`${API}/agriculture/lands`, { headers: headers() })
    ]).then(([f, c, l]) => { setTransactions(f.data.data); setCrops(c.data.data); setLands(l.data.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const fetchCategories = async () => {
    try {
      const [exp, inc, cropNames, locs, landsLoc] = await Promise.all([
        axios.get(`${API}/agriculture/settings/expense_category`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/income_category`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/crop_name`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/location`, { headers: headers() }),
        axios.get(`${API}/agriculture/lands-with-location`, { headers: headers() })
      ]);
      const expValues = (exp.data.data || []).map(item => item.value);
      const incValues = (inc.data.data || []).map(item => item.value);
      if (expValues.length > 0) setExpenseCats(expValues);
      if (incValues.length > 0) setIncomeCats(incValues);
      setSettingsCrops(cropNames.data.data || []);
      setLocations(locs.data.data || []);
      setLandsWithLoc(landsLoc.data.data || []);
      setCatsLoaded(true);
    } catch (err) {
      console.warn('Settings API unavailable, using fallback categories', err);
      setCatsLoaded(true);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchCategories();
  }, []);

  const openAdd = (type = 'expense') => {
    setForm({ ...EMPTY, transaction_type: type });
    setEditId(null);
    setGroupMode(false);
    setGroupLocationId('');
    setShowForm(true);
  };
  const openEdit = (t) => {
    setForm({ ...t, transaction_date: t.transaction_date?.split('T')[0] || '' });
    setEditId(t.id);
    setGroupMode(false);
    setGroupLocationId('');
    setShowForm(true);
  };

  const handleSave = async () => {
    // Group expense mode
    if (groupMode && !editId && form.transaction_type === 'expense') {
      if (!groupLocationId) return alert('Please select a location group');
      if (!form.category || !form.amount || !form.transaction_date) return alert('Category, amount and date are required');
      setSaving(true);
      try {
        await axios.post(`${API}/agriculture/finance/group-expense`, {
          location_id: groupLocationId,
          category: form.category,
          amount: form.amount,
          description: form.description,
          transaction_date: form.transaction_date,
          payment_method: form.payment_method,
          reference_no: form.reference_no
        }, { headers: headers() });
        setShowForm(false); fetchAll();
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to distribute group expense');
      }
      setSaving(false);
      return;
    }

    // Normal single entry
    if (!form.category || !form.amount || !form.transaction_date) return alert('Category, amount and date are required');
    setSaving(true);
    try {
      if (editId) await axios.put(`${API}/agriculture/finance/${editId}`, form, { headers: headers() });
      else await axios.post(`${API}/agriculture/finance`, form, { headers: headers() });
      setShowForm(false); fetchAll();
    } catch { alert('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    await axios.delete(`${API}/agriculture/finance/${id}`, { headers: headers() });
    fetchAll();
  };

  const fmt = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
  const totalIncome = transactions.filter(t => t.transaction_type === 'income').reduce((s, t) => s + parseFloat(t.amount), 0);
  const totalExpense = transactions.filter(t => t.transaction_type === 'expense').reduce((s, t) => s + parseFloat(t.amount), 0);
  const filtered = filter === 'all' ? transactions : transactions.filter(t => t.transaction_type === filter);
  const { paginatedData, currentPage, setCurrentPage, totalPages } = usePagination(filtered, 10);

  const getCategoryOptions = () => {
    const baseList = form.transaction_type === 'income' ? incomeCats : expenseCats;
    if (form.category && !baseList.includes(form.category)) {
      return [form.category, ...baseList];
    }
    return baseList;
  };

  // ── Group expense preview calculation (mirror of backend logic) ──
  const groupPreview = (() => {
    if (!groupMode || !groupLocationId || !form.amount) return null;
    const amount = Math.round(parseFloat(form.amount || 0));
    if (amount <= 0) return null;

    // Get target lands
    let targetLands;
    if (groupLocationId === 'all') {
      targetLands = landsWithLoc;
    } else {
      targetLands = landsWithLoc.filter(l => String(l.location_id) === String(groupLocationId));
    }
    if (targetLands.length === 0) return { lands: [], totalKanals: 0, error: 'No lands in this group' };

    const totalKanals = targetLands.reduce((s, l) => s + parseFloat(l.total_kanals || 0), 0);
    if (totalKanals <= 0) return { lands: [], totalKanals: 0, error: 'Lands have no kanal area' };

    // Sort by kanals desc (largest first, to absorb remainder)
    const sorted = [...targetLands].sort((a, b) => parseFloat(b.total_kanals || 0) - parseFloat(a.total_kanals || 0));
    const shares = sorted.map(l => {
      const kanals = parseFloat(l.total_kanals || 0);
      return { field_name: l.field_name, kanals, rounded: Math.round((kanals / totalKanals) * amount) };
    });
    const sumRounded = shares.reduce((s, x) => s + x.rounded, 0);
    const remainder = amount - sumRounded;
    if (remainder !== 0 && shares.length > 0) shares[0].rounded += remainder;

    return { lands: shares, totalKanals, error: null };
  })();

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading...</div>;

  const isExpense = form.transaction_type === 'expense';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>Finance</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => openAdd('income')} style={{ padding: '12px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Income</button>
          <button onClick={() => openAdd('expense')} style={{ padding: '12px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Expense</button>
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Income', value: fmt(totalIncome), color: '#27ae60', icon: '💰' },
          { label: 'Total Expenses', value: fmt(totalExpense), color: '#e74c3c', icon: '📉' },
          { label: 'Net Profit/Loss', value: fmt(totalIncome - totalExpense), color: totalIncome - totalExpense >= 0 ? '#27ae60' : '#e74c3c', icon: '📊' },
        ].map(c => (
          <div key={c.label} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${c.color}` }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>{c.icon}</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['all', 'income', 'expense'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: filter === f ? '#27ae60' : '#f0f0f0', color: filter === f ? 'white' : '#555'
          }}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['Date', 'Type', 'Category', 'Description', 'Crop', 'Field', 'Amount', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No transactions found.</td></tr>
            ) : paginatedData.map((t, i) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '14px 16px', color: '#555' }}>{new Date(t.transaction_date).toLocaleDateString('en-PK')}</td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: t.transaction_type === 'income' ? '#27ae6020' : '#e74c3c20', color: t.transaction_type === 'income' ? '#27ae60' : '#e74c3c' }}>
                    {t.transaction_type}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{t.category}</td>
                <td style={{ padding: '14px 16px', color: '#555', maxWidth: '200px' }}>{t.description || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{t.crop_name || '—'}</td>
                <td style={{ padding: '14px 16px', color: '#555' }}>{t.field_name || '—'}</td>
                <td style={{ padding: '14px 16px', fontWeight: '700', color: t.transaction_type === 'income' ? '#27ae60' : '#e74c3c' }}>{fmt(t.amount)}</td>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => openEdit(t)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                    <button onClick={() => handleDelete(t.id)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalRecords={filtered.length}
          rowsPerPage={10}
        />
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '24px' }}>{editId ? 'Edit Transaction' : `Add ${isExpense ? 'Expense' : 'Income'}`}</h3>

            {/* Distribute to Group toggle — only for NEW expenses */}
            {!editId && isExpense && (
              <div style={{ marginBottom: '20px', padding: '14px 16px', background: groupMode ? '#e8f6f3' : '#f8f9fa', borderRadius: '10px', border: `1px solid ${groupMode ? '#16a085' : '#eee'}` }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={groupMode} onChange={e => { setGroupMode(e.target.checked); setGroupLocationId(''); }} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>📍 Distribute to a Location Group</span>
                </label>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '6px', marginLeft: '28px' }}>
                  Split this expense across all lands in a group, proportional to each land's area (kanals).
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {/* Type — hidden in group mode (always expense) */}
              {!groupMode && (
                <div>
                  <label style={labelStyle}>Type</label>
                  <select style={inputStyle} value={form.transaction_type} onChange={e => setForm({ ...form, transaction_type: e.target.value, category: '' })}>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
              )}

              <div style={groupMode ? { gridColumn: '1/-1' } : {}}>
                <label style={labelStyle}>Category *</label>
                <select style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="">{catsLoaded ? '-- Select --' : 'Loading...'}</option>
                  {getCategoryOptions().map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Amount (Rs) *</label>
                <input style={inputStyle} type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label style={labelStyle}>Date *</label>
                <input style={inputStyle} type="date" value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} />
              </div>

              {/* Group mode: location dropdown + preview */}
              {groupMode ? (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Location Group *</label>
                  <select style={inputStyle} value={groupLocationId} onChange={e => setGroupLocationId(e.target.value)}>
                    <option value="">-- Select Group --</option>
                    <option value="all">🌍 All Lands</option>
                    {locations.map(l => <option key={l.id} value={l.id}>📍 {l.value}</option>)}
                  </select>

                  {/* Live preview */}
                  {groupPreview && (
                    <div style={{ marginTop: '14px', padding: '14px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #e0e0e0' }}>
                      {groupPreview.error ? (
                        <div style={{ fontSize: '13px', color: '#e74c3c' }}>⚠️ {groupPreview.error}</div>
                      ) : (
                        <>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#16a085', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Distribution Preview ({groupPreview.lands.length} lands, {groupPreview.totalKanals} kanals)
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {groupPreview.lands.map((l, idx) => (
                              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: 'white', borderRadius: '6px', fontSize: '13px' }}>
                                <span style={{ color: '#555' }}>🗺️ {l.field_name} <span style={{ color: '#aaa', fontSize: '12px' }}>({l.kanals} kanals)</span></span>
                                <span style={{ fontWeight: '700', color: '#2c3e50' }}>Rs {l.rounded.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #ccc', display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '700' }}>
                            <span style={{ color: '#555' }}>Total</span>
                            <span style={{ color: '#16a085' }}>Rs {groupPreview.lands.reduce((s, l) => s + l.rounded, 0).toLocaleString()}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label style={labelStyle}>Linked Field</label>
                    <select style={inputStyle} value={form.land_id} onChange={e => setForm({ ...form, land_id: e.target.value })}>
                      <option value="">-- None --</option>
                      {lands.map(l => <option key={l.id} value={l.id}>{l.field_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Linked Crop</label>
                    <select style={inputStyle} value={form.crop_id} onChange={e => setForm({ ...form, crop_id: e.target.value })}>
                      <option value="">-- None --</option>
                      {crops
                        .filter(c => {
                          const settingsNames = settingsCrops.map(s => s.value.toLowerCase().trim());
                          const isInSettings = settingsNames.includes((c.crop_name || '').toLowerCase().trim());
                          const isCurrentlySelected = String(c.id) === String(form.crop_id);
                          return isInSettings || isCurrentlySelected;
                        })
                        .map(c => (
                          <option key={c.id} value={c.id}>
                            {c.crop_name}{c.crop_type ? ` - ${c.crop_type}` : ''}{c.field_name ? ` (${c.field_name})` : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                </>
              )}

              <div>
                <label style={labelStyle}>Payment Method</label>
                <select style={inputStyle} value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Reference No</label>
                <input style={inputStyle} value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })} placeholder="Optional" />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Description</label>
                <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Details..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: groupMode ? '#16a085' : '#27ae60', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : groupMode ? 'Distribute Expense' : editId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceTab;